/**
 * Coding Editor - CodeMirror 6 통합 에디터
 * @class CodingEditor
 */

import { EditorState } from "@codemirror/state";
import { EditorView, keymap, highlightActiveLine, dropCursor, drawSelection, highlightSpecialChars, highlightActiveLineGutter, lineNumbers, rectangularSelection, ViewPlugin } from "@codemirror/view";
import { indentOnInput, syntaxHighlighting, defaultHighlightStyle, bracketMatching, foldGutter, foldKeymap } from "@codemirror/language";
import { history, historyKeymap, defaultKeymap } from "@codemirror/commands";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { autocompletion, completionKeymap } from "@codemirror/autocomplete";
import { lintKeymap } from "@codemirror/lint";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { javascript } from "@codemirror/lang-javascript";

class CodingEditor {
  constructor(containerId = null, initialContent = '', lang = 'html') {
    this.editor = null; // 단일 EditorView 인스턴스
    this.containerId = containerId; // 에디터 컨테이너 ID
    this.lang = lang; // 에디터 언어 (html, css, javascript)
    this.updatePreviewDebounced = this.debounce(this.updatePreview.bind(this), 500);
    this.previewUrl = null;
    this.changeCallback = null; // onChange 콜백
    
    // containerId가 제공되면 개별 에디터로 초기화
    if (containerId) {
      this.initEditor(containerId, initialContent);
    }
  }
  
  /**
   * CodeMirror 6 에디터 초기화
   * @param {string} containerId - 에디터를 마운트할 DOM 엘리먼트 ID
   * @param {string} initialContent - 초기 내용
   */
  initEditor(containerId = 'unified-editor', initialContent = '') {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`${containerId} 컨테이너를 찾을 수 없습니다`);
      return;
    }
    
    try {
      // 언어 익스텐션 선택
      let langExtension;
      switch (this.lang) {
        case 'css':
          langExtension = css();
          break;
        case 'js':
        case 'javascript':
          langExtension = javascript();
          break;
        case 'html':
        default:
          langExtension = html({ 
            matchClosingTags: true, // 태그 매칭 활성화
            selfClosingTags: true 
          }); 
          break;
      }

      // EditorState 생성
      const extensions = [
        // basicSetup 대신 개별 익스텐션 등록 (closeBrackets 제외 - 한글 입력 시 충돌)
        lineNumbers(), // 줄 번호 표시
        highlightActiveLineGutter(), // 활성 라인 번호 강조
        highlightSpecialChars(), // 특수 문자 가시화
        history(), // 실행 취소/다시 실행 히스토리
        foldGutter(), // 코드 접기(Fold) 영역 표시
        drawSelection({ cursorBlinkRate: 0 }), // 내부 깜빡임 비활성화 (CSS로 일괄 통제)
        dropCursor(), // 드래그 앤 드롭 시 커서 위치 표시
        rectangularSelection(), // Alt + 드래그로 사각형 영역 선택
        // Alt 키 상태에 따른 커서 변경 플러그인
        ViewPlugin.fromClass(class {
          constructor(view) {
            this.view = view;
            this.updateCursor = (e) => {
              if (e.key === 'Alt') {
                view.dom.classList.toggle('cm-alt-pressed', e.type === 'keydown');
              }
            };
            window.addEventListener('keydown', this.updateCursor);
            window.addEventListener('keyup', this.updateCursor);
          }
          destroy() {
            window.removeEventListener('keydown', this.updateCursor);
            window.removeEventListener('keyup', this.updateCursor);
          }
        }),
        EditorState.allowMultipleSelections.of(true), // 다중 선택 허용
        indentOnInput(), // 자동 들여쓰기
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }), // 구문 하이라이팅
        // bracketMatching(), // 괄호 매칭 비활성화 (한글 입력 시 충돌)
        autocompletion(), // 자동 완성
        highlightActiveLine(), // 현재 줄 하이라이트
        highlightSelectionMatches(), // 선택된 텍스트와 동일한 단어 강조
        keymap.of([
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...completionKeymap,
          ...lintKeymap
        ]),
        langExtension,
        EditorView.lineWrapping,
        // 선택 영역 스타일 테마
        EditorView.theme({
          "&.cm-alt-pressed": {
            cursor: "crosshair"
          },
          "&.cm-alt-pressed .cm-content": {
            cursor: "crosshair"
          },
          "&.cm-focused .cm-selectionBackground, ::selection": {
            backgroundColor: "#b3d7ff !important"
          },
          ".cm-selectionBackground, &:not(.cm-focused) .cm-selectionBackground": {
            backgroundColor: "#d7d4f0 !important"
          },
          "&.cm-focused .cm-cursor, &.cm-focused .cm-cursor-primary": {
            borderLeftColor: "#000000",
            animation: "cm-blink 1.2s steps(1) infinite"
          },
          ".cm-cursor, .cm-cursor-primary": {
            borderLeftColor: "transparent"
          },
          "@keyframes cm-blink": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0 }
          }
        })
      ];
      
      // onChange 콜백 등록
      extensions.push(
          EditorView.updateListener.of((update) => {
          if (update.docChanged && this.changeCallback) {
            this.changeCallback();
            }
          })
      );
      
      // 개별 에디터(containerId가 있을 때)는 빈 문자열로 시작
      // 통합 에디터는 기본 템플릿 사용
      let content = initialContent;
      if (!content && containerId === 'unified-editor') {
        content = this.getDefaultTemplate();
      }
      
      const startState = EditorState.create({
        doc: content || '',
        extensions: extensions
      });
      
      // EditorView 생성
      this.editor = new EditorView({
        state: startState,
        parent: container
      });
    } catch (error) {
      console.error(`❌ 에디터 초기화 실패 (${containerId}):`, error);
    }
  }
  
  /**
   * onChange 콜백 등록
   * @param {Function} callback 
   */
  onChange(callback) {
    this.changeCallback = callback;
  }
  
  /**
   * 기본 템플릿 반환 (HTML, CSS, JS 통합)
   * @returns {string}
   */
  getDefaultTemplate() {
    const templates = this.getDefaultTabTemplates();
    return this.combineCode(templates.html, templates.css, templates.js);
  }
  
  /**
   * 탭별 기본 템플릿 반환
   * @returns {{html: string, css: string, js: string}}
   */
  getDefaultTabTemplates() {
    return {
      html: `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>AI코딩 에디터</title>
</head>
<body>
  
</body>
</html>`,
      css: ``,
      js: ``
    };
  }
  
  /**
   * 미리보기 업데이트
   * @param {string} fullCode - 미리보기할 전체 HTML 코드 (선택 사항)
   */
  updatePreview(fullCode = null) {
    // fullCode가 제공되지 않으면 에디터 내용 사용
    if (!fullCode && this.editor) {
      fullCode = this.editor.state.doc.toString();
    }
    
    if (!fullCode) return;
    
    const iframe = document.getElementById('preview-frame');
    if (!iframe) {
      console.error('Preview iframe not found');
      return;
    }
    
    // Blob을 이용한 URL 생성
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    const blob = new Blob([fullCode], { type: 'text/html' });
    this.previewUrl = URL.createObjectURL(blob);
    iframe.src = this.previewUrl;
  }
  
  /**
   * HTML, CSS, JS 코드 결합 (통합 에디터에서는 그대로 사용)
   * @param {string} html 
   * @param {string} css 
   * @param {string} js 
   * @returns {string}
   */
  combineCode(html, css, js) {
    let combined = html;
    
    // CSS 삽입
    if (css && css.trim()) {
      const styleTag = `<style>\n${css}\n</style>`;
      
      if (combined.includes('</head>')) {
        combined = combined.replace('</head>', `${styleTag}\n</head>`);
      } else if (combined.includes('<head>')) {
        combined = combined.replace('<head>', `<head>\n${styleTag}`);
      } else {
        combined = styleTag + '\n' + combined;
      }
    }
    
    // JavaScript 삽입
    if (js && js.trim()) {
      const scriptTag = `<script>\n${js}\n<\/script>`;
      
      if (combined.includes('</body>')) {
        combined = combined.replace('</body>', `${scriptTag}\n</body>`);
      } else {
        combined = combined + '\n' + scriptTag;
      }
    }
    
    return combined;
  }
  
  /**
   * 전체 코드 가져오기
   * @returns {string}
   */
  getAllCode() {
    return this.editor ? this.editor.state.doc.toString() : '';
  }
  
  /**
   * 전체 코드 설정 (CodeMirror 6 방식)
   * @param {string} code 
   */
  setAllCode(code) {
    if (!this.editor || !this.editor.state) {
      console.warn('에디터가 준비되지 않았습니다.');
      return;
    }
    
    try {
      // 현재 상태에서 문서 길이 확인
      const currentLength = this.editor.state.doc.length;
      
      // 트랜잭션 생성 및 적용
      this.editor.dispatch({
      changes: {
        from: 0,
          to: currentLength,
          insert: code || ''
      }
    });
    } catch (error) {
      console.error('setAllCode 오류:', error);
      // 에러 발생 시 재시도
      try {
        // 에디터 상태를 새로 생성하여 재설정
        const newState = EditorState.create({
          doc: code || '',
          extensions: this.editor.state.extensions
        });
        this.editor.setState(newState);
      } catch (retryError) {
        console.error('setAllCode 재시도 실패:', retryError);
      }
    }
  }
  
  /**
   * 코드를 HTML, CSS, JS로 분리
   * @param {string} fullCode 
   * @returns {{html: string, css: string, js: string}}
   */
  parseCode(fullCode) {
    let html = fullCode;
    let css = '';
    let js = '';
    
    // CSS 추출 (<style> 태그)
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    const styleMatches = [...fullCode.matchAll(styleRegex)];
    if (styleMatches.length > 0) {
      css = styleMatches.map(m => m[1]).join('\n');
      html = html.replace(styleRegex, '');
    }
    
    // JS 추출 (<script> 태그)
    const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
    const scriptMatches = [...fullCode.matchAll(scriptRegex)];
    if (scriptMatches.length > 0) {
      js = scriptMatches.map(m => m[1]).join('\n');
      html = html.replace(scriptRegex, '');
    }
    
    return { html, css, js };
  }
  
  /**
   * 디바운스 유틸리티
   * @param {Function} func 
   * @param {number} wait 
   * @returns {Function}
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  /**
   * 에디터 리프레시 (CodeMirror 6에서는 자동 처리되나, v-show 등으로 숨겨졌다 나타날 때 필요할 수 있음)
   */
  refresh() {
    if (this.editor) {
      try {
        // 레이아웃 재계산 요청
        this.editor.requestMeasure();
        
        // 뷰포트가 비어있거나 렌더링이 멈춘 경우를 대비해 강제 업데이트
        // 현재 상태를 유지하며 트랜잭션 발생
        this.editor.dispatch({
          selection: this.editor.state.selection,
          scrollIntoView: false
        });
        
        // 전체화면 모드 등에서 스타일이 제대로 적용되도록 강제 업데이트
        // 약간의 지연을 두어 DOM 업데이트 후 실행
        setTimeout(() => {
          if (this.editor) {
            this.editor.requestMeasure();
            // 뷰포트 업데이트를 강제로 트리거
            this.editor.dispatch({
              effects: [],
              annotations: []
            });
          }
        }, 50);
      } catch (error) {
        console.warn('에디터 refresh 실패:', error);
      }
    }
  }
  
  /**
   * 에디터 값 가져오기 (하위 호환성)
   */
  getValue(type) {
    const parsed = this.parseCode(this.getAllCode());
    return parsed[type] || '';
  }
  
  /**
   * 에디터 값 설정 (하위 호환성)
   */
  setValue(type, value) {
    const current = this.parseCode(this.getAllCode());
    current[type] = value;
    const combined = this.combineCode(current.html, current.css, current.js);
    this.setAllCode(combined);
  }
  
  /**
   * 레거시 호환성을 위한 editors 객체
   */
  get editors() {
    return {
      html: {
        getValue: () => this.getValue('html'),
        setValue: (v) => this.setValue('html', v),
        refresh: () => this.refresh()
      },
      css: {
        getValue: () => this.getValue('css'),
        setValue: (v) => this.setValue('css', v),
        refresh: () => this.refresh()
      },
      js: {
        getValue: () => this.getValue('js'),
        setValue: (v) => this.setValue('js', v),
        refresh: () => this.refresh()
      }
    };
  }
}


/**
 * Code Patcher - Diff/Patch 기반 코드 업데이트
 * @class CodePatcher
 */
class CodePatcher {
  constructor(editor) {
    this.editor = editor;
    // diff_match_patch는 전역으로 로드됨 (CDN)
    if (typeof diff_match_patch !== 'undefined') {
      this.dmp = new diff_match_patch();
    } else {
      console.warn('diff_match_patch가 로드되지 않았습니다');
    }
  }
  
  /**
   * Patch 적용
   * @param {string} oldCode 
   * @param {string} patch 
   * @returns {string}
   */
  applyPatch(oldCode, patch) {
    if (!this.dmp) return oldCode;
    
    try {
      const patches = this.dmp.patch_fromText(patch);
      const [newCode, results] = this.dmp.patch_apply(patches, oldCode);
      return newCode;
    } catch (error) {
      console.error('Patch 적용 오류:', error);
      return oldCode;
    }
  }
  
  /**
   * 스트리밍 Diff 적용 (CodeMirror 6)
   * @param {EditorView} editorInstance 
   * @param {string} newContent 
   */
  applyStreamingDiff(editorInstance, newContent) {
    if (!editorInstance) return;
    
    const oldValue = editorInstance.state.doc.toString();
    const cursorPos = editorInstance.state.selection.main.head;
    
    // 코드 교체
    const transaction = editorInstance.state.update({
      changes: {
        from: 0,
        to: editorInstance.state.doc.length,
        insert: newContent
      }
    });
    
    editorInstance.dispatch(transaction);
    
    // 커서 위치 복원 (가능한 경우)
    try {
      const newDocLength = editorInstance.state.doc.length;
      const safeCursorPos = Math.min(cursorPos, newDocLength);
      
      editorInstance.dispatch({
        selection: { anchor: safeCursorPos }
      });
    } catch (e) {
      // 커서 위치가 범위를 벗어난 경우 무시
    }
  }
  
  /**
   * Diff 생성
   * @param {string} oldCode 
   * @param {string} newCode 
   * @returns {string}
   */
  createDiff(oldCode, newCode) {
    if (!this.dmp) return '';
    
    const diffs = this.dmp.diff_main(oldCode, newCode);
    this.dmp.diff_cleanupSemantic(diffs);
    return this.dmp.diff_prettyHtml(diffs);
  }
  
  /**
   * Patch 텍스트 생성
   * @param {string} oldCode 
   * @param {string} newCode 
   * @returns {string}
   */
  createPatch(oldCode, newCode) {
    if (!this.dmp) return '';
    
    const diffs = this.dmp.diff_main(oldCode, newCode);
    const patches = this.dmp.patch_make(oldCode, diffs);
    return this.dmp.patch_toText(patches);
  }
}

// Export for Rollup bundle
export { CodingEditor, CodePatcher };
